/**
 * SP-API onboarding wizard hooks — Pass 5.
 *
 * Three endpoints power the five-step wizard:
 *   GET  /api/credentials/sp-api/status                    → which step to show
 *   POST /api/credentials/sp-api/mark-awaiting-approval    → step 1 click
 *   POST /api/credentials/sp-api/verify                    → step 5 click
 *
 * Saving the credential payload itself goes through the existing
 * useCreateCredential() hook with provider='sp_api'.
 */
import { useMutation, useQuery, useQueryClient, queryOptions } from '@tanstack/react-query';
import {
  SpApiMarkAwaitingApprovalResponse,
  SpApiStatusResponse,
  SpApiVerifyResponse,
} from '@sourcito/schemas';
import { apiFetch } from './api';

export const spApiStatusQuery = queryOptions({
  queryKey: ['sp-api', 'status'] as const,
  queryFn: () =>
    apiFetch<SpApiStatusResponse>('/api/credentials/sp-api/status', {
      responseSchema: SpApiStatusResponse,
    }),
  // Status changes only on explicit user action (save / verify / mark) →
  // 30s staleness is plenty.
  staleTime: 30_000,
});

export function useSpApiStatus() {
  return useQuery(spApiStatusQuery);
}

export function useMarkSpApiAwaitingApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<SpApiMarkAwaitingApprovalResponse>(
        '/api/credentials/sp-api/mark-awaiting-approval',
        {
          method: 'POST',
          responseSchema: SpApiMarkAwaitingApprovalResponse,
        }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sp-api'] });
    },
  });
}

export function useVerifySpApi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<SpApiVerifyResponse>('/api/credentials/sp-api/verify', {
        method: 'POST',
        responseSchema: SpApiVerifyResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sp-api'] });
      qc.invalidateQueries({ queryKey: ['credentials'] });
    },
  });
}
